/* One recorded render failure and its internal correlation reference.
 *
 * A render that throws is this console's safe-stop: the screen stops rather than paint
 * something incorrect. Stopping *silently* was the defect this module closes — the console
 * shipped with the router's built-in error screen, no reporting hook on any boundary, and
 * `capture_console_errors: false`, so a crash reached the operator and reached nobody else.
 * The screen said "Something went wrong!" and that was the entire incident record.
 *
 * Minting and reporting are separate because a boundary may render before it can report:
 *
 *   - `referenceFor` mints and remembers without a side effect. The record is internal;
 *     rendering must not report while React is reconciling, and it has no component stack.
 *   - `reportRenderIncident` is called from `onCatch`, which runs after that render and is the
 *     only place the component stack exists. It reports under the same reference.
 *
 * Both are idempotent per error instance, keyed on the thrown object. React calls `onCatch`
 * once but re-renders a fallback freely, so this keeps one failure to one recorded event.
 *
 * The reference is a correlation handle, not a secret. It stays in observability, with the
 * deployed build and route that produced it, rather than asking the person to carry it.
 *
 * **Where each record lives, and what is not allowed to page.** Both records below are spans on
 * service `sx-console`, and both carry `sampling.priority: 1`, so the
 * collector keeps 100 % of them against the traces pipeline's 10 % sampling. What differs is the
 * span *status*, and that difference is the whole design:
 *
 *   - `console.failure` — one failed read or write, status **UNSET**. A 403 on a page someone is
 *     not entitled to, a 404, a 409, an offline laptop's reads: every one is an ordinary product
 *     answer, and none of them is this service failing. The collector's `spanmetrics` connector
 *     folds only ERROR spans into the door availability SLO's ratio, so leaving these
 *     UNSET is what keeps a door's refusal from waking someone at 03:00. The record is complete
 *     either way, and the count comes from `traces_span_metrics_calls_total` by `door` and
 *     `failure_kind` — the two dimensions `collectors.yaml` asks the connector for.
 *   - `console.render_failure` — one render throw, status **ERROR**. The screen stopped; that is
 *     this service failing, it *should* page, and it is also sent to PostHog Error Tracking for
 *     issue grouping and debugging. Catching it at a boundary changes the screen, not its status
 *     as a production defect.
 *
 * A span rather than a browser-side metric because a browser cannot write a metric series the
 * collector can aggregate — see the head of `@sx/observability/recorder` for why. So the span is
 * the record, Tempo is where a person reads one, and every number is derived from the same spans
 * by the connector. Nothing is counted twice and nothing is sampled away.
 *
 * `recordFailure` is total, filters credential-shaped attribute *keys* and bounds every string at
 * 4 KiB. The filter does not read values: what a door's own message may contain is decided where
 * the message is put on the span, which is `errorMessage` below.
 */

import { ApiError, type FailureReport } from "@sx/api-client";
import { recordFailure } from "@sx/observability/recorder";

import { CONSOLE_DOORS } from "../platform.gen";
import { captureRenderException } from "../posthog";

/** Crockford base32: no I, L, O, or U, so a reference survives being read down a phone
 * line or retyped from a screenshot. */
const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const REFERENCE_BYTES = 8;

interface Incident {
  readonly reference: string;
  reported: boolean;
}

/* Keyed by the thrown object where there is one. A thrown primitive — `throw "boom"` is
 * legal, and the failure screen is the last place that may assume otherwise — has no
 * identity to hang a WeakMap off, so it is keyed by its own text instead. Without that
 * second store a primitive throw renumbers its reference on every re-render and reports
 * again each time, which breaks the one promise this screen makes: that the reference on
 * screen is the reference in the record. */
const byObject = new WeakMap<object, Incident>();
const byValue = new Map<string, Incident>();
/** A leak bound, not a cache policy: primitive throws are rare and their keys are tiny,
 *  but nothing here may grow without a ceiling. */
const VALUE_KEYS_MAX = 64;

function mint(): string {
  const bytes = new Uint8Array(REFERENCE_BYTES);
  try {
    crypto.getRandomValues(bytes);
  } catch {
    // Never let the failure screen fail. A weak reference costs a support lookup; a throw
    // here costs the operator the only screen that could tell them what to do next.
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  const body = Array.from(
    bytes,
    (byte) => ALPHABET[byte % ALPHABET.length] ?? "0",
  ).join("");
  return `SX-${body.slice(0, 4)}-${body.slice(4)}`;
}

/** The one record for one failure, created on first sight of it. */
function incidentFor(error: unknown): Incident {
  if (typeof error === "object" && error !== null) {
    const found = byObject.get(error);
    if (found !== undefined) return found;
    const created: Incident = { reference: mint(), reported: false };
    byObject.set(error, created);
    return created;
  }

  const key = `${typeof error}:${String(error)}`;
  const found = byValue.get(key);
  if (found !== undefined) return found;
  const created: Incident = { reference: mint(), reported: false };
  if (byValue.size >= VALUE_KEYS_MAX) byValue.clear();
  byValue.set(key, created);
  return created;
}

/** The internal reference for this failure, minting one on first sight. */
export const referenceFor = (error: unknown): string =>
  incidentFor(error).reference;

/** Which boundary stopped. Route-level replaces the page; component-level leaves the rest
 * of the workspace usable, and the two want different triage. */
export type IncidentSurface = "route" | "component";

export interface IncidentContext {
  readonly surface: IncidentSurface;
  /** The failing component's own name, for a component-level boundary. */
  readonly component?: string;
  readonly componentStack?: string;
}

/** Report one render failure under its reference, once. Returns that reference. */
export function reportRenderIncident(error: unknown, context: IncidentContext): string {
  const incident = incidentFor(error);
  const reference = incident.reference;
  if (incident.reported) return reference;
  incident.reported = true;
  latest = reference;

  // Local debugging keeps the real stack in the browser console, where the person sitting in
  // front of the failure can read it; the span carries what triage can group on.
  console.error(`[${reference}]`, error);
  recordFailure(
    "console.render_failure",
    {
      incident_reference: reference,
      incident_surface: context.surface,
      incident_component: context.component,
      component_stack: context.componentStack,
      build_id: __BUILD_ID__,
      pathname: window.location.pathname,
      error_type: errorType(error),
      error_message: errorMessage(error),
    },
    // The screen stopped. This is the one console span that should page.
    { status: "error" },
  );
  captureRenderException(error, {
    incident_reference: reference,
    incident_surface: context.surface,
    incident_component: context.component,
    component_stack: context.componentStack,
    build_id: __BUILD_ID__,
    pathname: window.location.pathname,
  });
  return reference;
}

/** A caught operational failure whose effect has already been contained.
 *
 * It remains visible in Grafana, but it is neither a render incident nor an ERROR span and never
 * enters PostHog Error Tracking. The closed operation name keeps the spanmetrics vocabulary
 * bounded as more handled browser effects are admitted. */
export type HandledFailureOperation =
  | "rendered_view_disconnect"
  | "world_appearance_load"
  | "world_headset_enter";

export function reportHandledFailure(
  error: unknown,
  operation: HandledFailureOperation,
): void {
  recordFailure(
    "console.handled_failure",
    {
      failure_operation: operation,
      pathname: window.location.pathname,
      error_type: errorType(error),
      error_message: errorMessage(error),
    },
    { status: "unset" },
  );
}

/** The error's *type*: a constructor name, which is what a `spanmetrics` dimension or a Tempo
 * query groups on. Closed enough to group, and never a value a door chose. */
function errorType(error: unknown): string {
  return error instanceof Error ? error.name : typeof error;
}

/* The error's message.
 *
 * A signature with no cause is a row nobody can act on, so the message travels — but it is
 * whatever the door wrote, and a door may echo a path segment or an identifier into it. Two
 * bounds apply: it is cut at 256 characters, which is a cause and not a payload, and it is never
 * offered as a `spanmetrics` dimension, so it can never become a Prometheus label. The stack is
 * not sent at all; it stays in the browser console for the person in front of the failure. */
const MAX_MESSAGE = 256;

function bounded(value: string): string {
  return value.slice(0, MAX_MESSAGE);
}

function errorMessage(error: unknown): string {
  return bounded(error instanceof Error ? error.message : String(error));
}

/* The reference of the most recent reported failure, so the shell's "Report this"
 * control can carry it. It is deliberately the *reported* one — a reference minted
 * during a render that then succeeded is not an incident — and deliberately the last
 * one rather than a list: the person is reporting the thing in front of them. */
let latest: string | undefined;

/** The last reported failure's reference, or `undefined` when nothing has failed in
 * this document. Absence is the ordinary case: most reports are not about a crash. */
export const latestIncidentReference = (): string | undefined => latest;

const DOOR_IDS: ReadonlySet<string> = new Set(CONSOLE_DOORS.map((door) => door.id));

/** The door a failure belongs to: the query key's, when it follows the door-keyed shape, else
 * the one the transport boundary stamped on the error — a write has no key, and its `ApiError`
 * still knows which door did not answer.
 *
 * Both paths are checked against the generated registry, and that is not symmetry for its own
 * sake: `door` is a `spanmetrics` dimension, so whatever reaches it becomes a Prometheus label
 * value and therefore a permanent time series. `ApiError.door` is typed `string | undefined`
 * rather than a door id, so every construction site passing a generated id is a convention, and
 * a convention is not a bound. An unrecognised door reads as absent, which is what an absent
 * door already reads as. */
function doorOf(key: readonly unknown[], error: unknown): string | undefined {
  const head = key[0];
  if (typeof head === "string" && DOOR_IDS.has(head)) return head;
  const stamped = error instanceof ApiError ? error.door : undefined;
  return stamped !== undefined && DOOR_IDS.has(stamped) ? stamped : undefined;
}

/** Record one failed read or write, as the query cache saw it.
 *
 * A render throw is rare and loud; a failed read is common and, until this module existed,
 * silent — the screen said "did not load" and nothing else did. The query client calls this once
 * per failure episode, so the record reflects how often people met a failure rather than how
 * often the cache retried.
 *
 * Status UNSET, deliberately: see the head of this file. `door` and `failure_kind` are the two
 * attributes the `spanmetrics` connector is configured to carry into Prometheus, and both are
 * closed vocabularies — a door id checked against the generated registry, and `read` or `write`
 * — so the label sets they produce are bounded. Everything else here is for a person reading one
 * span in Tempo. */
export function reportFailure(failure: FailureReport): void {
  recordFailure(
    "console.failure",
    {
      failure_kind: failure.kind,
      failure_transient: failure.transient,
      failure_had_snapshot: failure.hadSnapshot,
      door: doorOf(failure.key, failure.error),
      error_type: errorType(failure.error),
      // The status is the single most useful fact for telling an outage from a refusal, and it
      // only exists when the transport boundary knew one.
      http_status: failure.error instanceof ApiError ? failure.error.status : undefined,
      pathname: window.location.pathname,
      query_key: bounded(failure.key.map(String).join("/")),
    },
    { status: "unset" },
  );
}
